import { BaseComponent } from './BaseComponent.js';
import { EventBus, EVENTS } from '../../services/EventBus.js';
import { SwitchComponent } from './SwitchComponent.js';
import { RulesetCodeConverter } from './RulesetCodeConverter.js';
import { ICONS } from '../icons.js';
import { rulesetName, downloadFile } from '../../utils/utils.js';
import {
    classifyRulesetConstraint,
    satisfiesRulesetConstraint,
    CONSTRAINT_CLASSES,
    CONSTRAINT_CLASS_META,
} from '../../core/rulesetDescriptor.js';
import * as InitialStateCodec from '../../services/InitialStateCodec.js';
import * as PersistenceService from '../../services/PersistenceService.js';
import { decodePack, toPublicLibraryEntry } from '../../services/LibraryPackCodec.js';
import { validateSubmission, buildSubmissionIssue } from '../../services/LibrarySubmission.js';

/**
 * The Ruleset Library menu: load saved rulesets (public + personal) or set one
 * directly from a hex code. Split out of RulesetActionsComponent so the library
 * lives behind its own toolbar button. A single "Apply to:" scope selector at the
 * top governs both the Library loads and the Direct hex set (shared, persisted
 * `globalRulesetScopeAll` setting via RulesetActionController).
 */
export class RulesetLibraryComponent extends BaseComponent {
    constructor(appContext, options = {}) {
        super(null, options);
        this.appContext = appContext;

        this.libraryData = options.libraryData;
        // One combined list, with source as a filter rather than a separate Public/Personal tab.
        this.filterState = { query: '', tag: null, constraint: null, source: 'all', sort: 'recent' };
        // What "Load" means for an entry that carries a paired initial condition (roadmap #30).
        // This used to be a second button on every such card; it is one list-level choice now,
        // sitting with the scope switch that already governs every load from this list. Default on:
        // the card's thumbnail is baked from that IC, so loading it is what the user just looked at.
        this.pairedStart = PersistenceService.loadUISetting('libraryPairedStart', true);
        // Handle for the in-flight lazy thumbnail backfill so we can cancel it on re-render/destroy.
        this._backfillHandle = null;
        // Keys of entries we've already tried to bake this pane-lifetime (success OR fail), so a save /
        // library change re-bakes only genuinely-new entries instead of sweeping the whole library again.
        this._backfillAttempted = new Set();
        this.element = document.createElement('div');
        this.element.className = 'ruleset-actions-container';

        this.render();
        this.attachEventListeners();
        this.setActivePane('library');
    }

    getElement() {
        return this.element;
    }

    render() {
        // The scope switch rides in the pane-tab row rather than owning a labelled line of its own:
        // it sits next to the Load / Set buttons it governs and costs no vertical space.
        this.element.innerHTML = `
            <div class="ruleset-actions-header">
                <div class="ruleset-actions-segments">
                    <button class="ruleset-actions-segment active" data-pane="library">Library</button>
                    <button class="ruleset-actions-segment" data-pane="direct">Direct</button>
                </div>
                <div class="ruleset-library-scope" id="ruleset-library-scope-mount" title="Which worlds a loaded ruleset is applied to"></div>
            </div>
            <div class="ruleset-actions-content">
                <div id="ruleset-library-library-pane" class="ruleset-pane"></div>
                <div id="ruleset-library-direct-pane" class="ruleset-pane hidden"></div>
            </div>
        `;

        this.panes = {
            library: this.element.querySelector('#ruleset-library-library-pane'),
            direct: this.element.querySelector('#ruleset-library-direct-pane'),
        };

        this.segments = {
            library: this.element.querySelector('[data-pane="library"]'),
            direct: this.element.querySelector('[data-pane="direct"]'),
        };

        this.actionsPopover = this.appContext.uiManager.actionsPopover;
        this.factory = this.appContext.rulesetDisplayFactory;

        new SwitchComponent(this.element.querySelector('#ruleset-library-scope-mount'), {
            ...this.appContext.rulesetActionController.getGenScopeSwitchConfig(),
            // No label row here — the item text carries the meaning on its own.
            label: '',
            items: [
                { value: 'selected', text: 'This world' },
                { value: 'all', text: 'All worlds' }
            ],
            name: 'ruleset-library-scope',
            initialValue: this.appContext.rulesetActionController.getGenScope(),
        });

        this._renderLibraryPane();
        this._renderDirectPane();
    }

    _renderLibraryPane() {
        const pane = this.panes.library;
        pane.innerHTML = `
            <div class="library-toolbar">
                <input type="search" class="library-search" placeholder="Search name, tag or code…" aria-label="Search rulesets" />
                <select class="library-sort" aria-label="Sort rulesets">
                    <option value="recent">Recent</option>
                    <option value="name">Name A–Z</option>
                </select>
                <button type="button" class="library-paired-toggle${this.pairedStart ? ' active' : ''}" data-action="toggle-paired-start" aria-pressed="${this.pairedStart}" title="When on, loading an entry that has a paired starting condition also re-seeds the world with it — the state its preview was made from. Off loads the rule alone and leaves your cells as they are.">Paired start</button>
                <button class="button-icon library-pack-btn" data-action="export-pack" title="Export your rulesets as a shareable pack file" aria-label="Export rulesets to a pack file">${ICONS.download}</button>
                <button class="button-icon library-pack-btn" data-action="import-pack" title="Import rulesets from a pack file" aria-label="Import rulesets from a pack file">${ICONS.upload}</button>
                <input type="file" class="library-import-input" accept="application/json,.json" hidden aria-hidden="true" />
            </div>
            <div class="library-constraint-filters" id="ruleset-library-constraint-filters"></div>
            <div class="library-tag-filters" id="ruleset-library-tag-filters"></div>
            <div class="library-source-controls" id="ruleset-library-source-controls"></div>
            <div id="ruleset-library-content" class="library-list"></div>
        `;

        this._renderConstraintFilters();
        this._renderTagFilters();
        this._renderSourceControls();
        this._renderLibrary();
        this._scheduleThumbnailBackfill();
    }

    /**
     * Apply the live search query + active source/tag/constraint filters + sort. Source is attached
     * only to the combined view model; it is never persisted onto a library entry.
     */
    _applyFilter(entries) {
        const { query, tag, constraint, source, sort } = this.filterState;
        const q = query.trim().toLowerCase();
        let out = entries.filter(rule => {
            if (source !== 'all' && rule.__source !== source) return false;
            if (tag && !(Array.isArray(rule.tags) && rule.tags.includes(tag))) return false;
            // Constraint filter is HIERARCHY-AWARE (unlike the badge, which names only the strictest
            // class): "R-sym" means "is rotationally symmetric", so it includes the n_count and
            // totalistic rules that also satisfy it. Anything else would make "show me the symmetric
            // rules" hide the most symmetric ones in the library.
            if (constraint && !this._satisfiesConstraint(rule.hex, constraint)) return false;
            if (!q) return true;
            // The constraint class is searchable but deliberately NOT a tag chip: it's derived from
            // the rule table, so it must never be stored on (or filtered as) entry metadata. Both the
            // badge wording ("N-count") and the raw class id ("n_count") match, since either is a
            // reasonable thing to type.
            const cls = classifyRulesetConstraint(rule.hex);
            const hay = [
                rule.name || '',
                rule.description || '',
                rulesetName(rule.hex),
                ...(Array.isArray(rule.tags) ? rule.tags : []),
                ...(cls ? [cls, CONSTRAINT_CLASS_META[cls].label] : []),
            ].join(' ').toLowerCase();
            return hay.includes(q);
        });
        if (sort === 'name') {
            out = [...out].sort((a, b) => (a.name || rulesetName(a.hex)).localeCompare(b.name || rulesetName(b.hex)));
        }
        return out;
    }

    /** Public catalog + personal saves as one source-marked view; personal entries lead in Recent. */
    _getCombinedEntries() {
        const personal = this.appContext.libraryController.getUserLibrary()
            .map(rule => ({ ...rule, __source: 'personal' }));
        const cache = PersistenceService.loadPublicThumbCache();
        const publicEntries = (this.libraryData?.rulesets || []).map(rule => ({
            ...rule,
            ...(cache[rule.hex] ? { thumb: cache[rule.hex] } : {}),
            __source: 'public',
        }));
        return [...personal, ...publicEntries];
    }

    /**
     * Whether `hex` satisfies constraint class `cls` — i.e. its own class is at least as strict.
     * CONSTRAINT_CLASSES is ordered strictest-first, so "at least as strict" is an index compare.
     */
    _satisfiesConstraint(hex, cls) {
        return satisfiesRulesetConstraint(hex, cls);
    }

    /** Build the union of all tags across public + personal entries as toggleable filter chips. */
    _renderTagFilters() {
        const mount = this.element.querySelector('#ruleset-library-tag-filters');
        if (!mount) return;
        const all = [
            ...(this.libraryData?.rulesets || []),
            ...this.appContext.libraryController.getUserLibrary(),
        ];
        const tags = [...new Set(all.flatMap(r => (Array.isArray(r.tags) ? r.tags : [])))].sort();
        mount.classList.toggle('hidden', tags.length === 0);
        mount.innerHTML = tags.map(t =>
            `<button class="tag-chip tag-filter${this.filterState.tag === t ? ' active' : ''}" data-tag-filter="${this._escapeAttr(t)}">${this._escape(t)}</button>`
        ).join('');
    }

    /**
     * Constraint-class filter chips. Their own row, above the tag chips: the class is DERIVED from
     * the rule table, never stored, so it must not look like (or mix with) the editable tag
     * vocabulary. Each chip shows how many entries satisfy it, and a class nobody satisfies is
     * omitted rather than offered as a dead end.
     */
    _renderConstraintFilters() {
        const mount = this.element.querySelector('#ruleset-library-constraint-filters');
        if (!mount) return;
        const all = [
            ...(this.libraryData?.rulesets || []),
            ...this.appContext.libraryController.getUserLibrary(),
        ];
        const chips = CONSTRAINT_CLASSES
            .map(cls => ({ cls, n: all.filter(r => this._satisfiesConstraint(r.hex, cls)).length }))
            // Drop chips that cannot narrow anything: none matching, or — because the filter is
            // hierarchy-aware — every ruleset matching (`Free` always does, since every rule is at
            // least free). A chip that returns the unfiltered list is noise, not an option.
            .filter(({ n }) => n > 0 && n < all.length)
            .map(({ cls, n }) => {
                const meta = CONSTRAINT_CLASS_META[cls];
                const active = this.filterState.constraint === cls;
                const title = `${meta.description} Includes any stricter class (${n} ruleset${n === 1 ? '' : 's'}).`;
                return `<button class="tag-chip constraint-filter constraint-${cls}${active ? ' active' : ''}" data-constraint-filter="${cls}" title="${this._escapeAttr(title)}" aria-pressed="${active}">${this._escape(meta.label)} <span class="constraint-filter-count">${n}</span></button>`;
            });
        mount.classList.toggle('hidden', chips.length === 0);
        mount.innerHTML = chips.join('');
    }

    _renderSourceControls() {
        const mount = this.element.querySelector('#ruleset-library-source-controls');
        if (!mount) return;
        const publicCount = this.libraryData?.rulesets?.length || 0;
        const userRulesets = this.appContext.libraryController.getUserLibrary();
        const duplicateCount = this.appContext.libraryController.getPublicDuplicateRulesets().length;
        const sources = [
            { id: 'all', label: 'All', count: publicCount + userRulesets.length },
            { id: 'public', label: 'Public', count: publicCount },
            { id: 'personal', label: 'Mine', count: userRulesets.length },
        ];
        const filters = sources.map(({ id, label, count }) => {
            const active = this.filterState.source === id;
            return `<button class="tag-chip source-filter${active ? ' active' : ''}" data-source-filter="${id}" aria-pressed="${active}">${label} <span class="source-filter-count">${count}</span></button>`;
        }).join('');
        const cleanup = duplicateCount > 0
            ? `<button type="button" class="button-subtle library-dedupe-btn" data-action="remove-public-duplicates" title="Remove personal copies of rulesets that are already in the public library">${ICONS.trash} Remove ${duplicateCount} duplicate${duplicateCount === 1 ? '' : 's'}</button>`
            : '';
        mount.innerHTML = `<div class="library-source-filters" aria-label="Filter rulesets by source">${filters}</div>${cleanup}`;
    }

    _renderLibrary() {
        const list = this.element.querySelector('#ruleset-library-content');
        list.innerHTML = '';
        const userRulesets = this.appContext.libraryController.getUserLibrary();
        const entries = this._applyFilter(this._getCombinedEntries());

        if (entries.length === 0 && this.filterState.source === 'personal' && userRulesets.length === 0) {
            list.innerHTML = `
                <div class="panel-empty-state">
                    <div class="panel-empty-state-icon">${ICONS.star}</div>
                    <p class="panel-empty-state-title">No saved rulesets yet</p>
                    <p class="panel-empty-state-desc">This is your personal collection. Find a rule you like, then click the <span class="inline-icon">${ICONS.star}</span> <strong>Save</strong> button in the top bar to keep it here.</p>
                </div>`;
            return;
        }
        if (entries.length === 0) {
            list.innerHTML = `<p class="empty-state-text">No rulesets match these filters.</p>`;
            return;
        }
        entries.forEach(rule => list.appendChild(
            this.factory.createLibraryListItem(rule, rule.__source === 'personal')
        ));
    }

    /**
     * Lazily bake evolved-world thumbnails (borrow-and-restore engine) for any library entry — personal
     * OR public — that carries a paired initial condition but has no thumbnail yet. Personal results are
     * persisted to the user library; public results go to the client-side public-thumb cache (the
     * committed JSON only stores the IC choice). Persists silently and swaps each card's image in place
     * to avoid a re-render storm. Cancels any prior run; skipped while Auto-Explore is running. Called
     * once per pane open / real library change (NOT on every search keystroke).
     */
    _scheduleThumbnailBackfill() {
        this._backfillHandle?.cancel();
        this._backfillHandle = null;
        const wm = this.appContext.worldManager;
        if (!wm?.backfillMissingThumbnails || wm.autoExploreService?.isRunning?.()) return;

        const cache = PersistenceService.loadPublicThumbCache();
        const seen = this._backfillAttempted;
        // Include entries WITHOUT a paired IC too — the bake engine falls back to the selected world's
        // current IC so favourited/plain rulesets get a preview. Skip anything already tried this pane
        // lifetime so a save doesn't re-launch a full-library sweep.
        const personal = this.appContext.libraryController.getUserLibrary()
            .filter(r => !r.thumb && !seen.has(`personal:${r.id}`))
            .map(r => ({ ...r, __scope: 'personal' }));
        const publicMissing = (this.libraryData?.rulesets || [])
            .filter(r => !cache[r.hex] && !seen.has(`public:${r.hex}`))
            .map(r => ({ ...r, __scope: 'public' }));
        const jobs = [...personal, ...publicMissing];
        if (jobs.length === 0) return;

        this._backfillHandle = wm.backfillMissingThumbnails(jobs, {
            max: 24,
            onResult: (entry, thumb) => {
                // Remember every attempt (success or fail) so later saves/re-renders skip it.
                seen.add(entry.__scope === 'personal' ? `personal:${entry.id}` : `public:${entry.hex}`);
                if (!thumb) return;
                if (entry.__scope === 'personal') {
                    this.appContext.libraryController.setUserRulesetThumb(entry.id, thumb, { silent: true });
                    this._applyThumbToCard(entry.id, null, thumb);
                } else {
                    PersistenceService.savePublicThumb(entry.hex, thumb);
                    this._applyThumbToCard(null, entry.hex, thumb);
                }
            },
            onDone: ({ cancelled, remaining }) => {
                // The per-sweep cap bakes at most `max` entries; if more remain (e.g. a >24-entry public
                // library), chain the next batch so the whole library fills in over successive sweeps
                // instead of stalling. `seen` now excludes this batch, so each pass advances and the
                // reschedule terminates once nothing missing is left. Skip if a newer sweep superseded us.
                if (cancelled || remaining <= 0) return;
                this._scheduleThumbnailBackfill();
            },
        });
    }

    /** Swap a card thumbnail in place (no list re-render). Match by id (personal) or public hex. */
    _applyThumbToCard(id, hex, thumb) {
        const list = this.element.querySelector('#ruleset-library-content');
        if (!list) return;
        const sel = id
            ? `.library-card[data-id="${CSS.escape(id)}"]`
            : `.library-card[data-source="public"][data-hex="${CSS.escape(hex)}"]`;
        const card = list.querySelector(sel);
        const box = card?.querySelector('.library-card-thumb');
        if (!box) return;
        const existing = box.querySelector('.viz-placeholder, .ruleset-viz-svg, .library-card-thumb-img');
        const img = document.createElement('img');
        img.className = 'library-card-thumb-img';
        img.alt = '';
        img.src = thumb;
        if (existing) existing.replaceWith(img); else box.prepend(img);
    }

    _escape(str) {
        return String(str).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }

    _escapeAttr(str) {
        return String(str).replace(/"/g, '&quot;');
    }

    _renderDirectPane() {
        const mountPoint = this.panes.direct;
        mountPoint.innerHTML = '';
        this.converter?.destroy();
        this.converter = new RulesetCodeConverter(mountPoint, this.appContext, { context: 'ruleset-library-direct' });
    }

    attachEventListeners() {
        this.element.querySelector('.ruleset-actions-header').addEventListener('click', e => {
            if (e.target.matches('.ruleset-actions-segment')) {
                this.setActivePane(e.target.dataset.pane);
            }
        });

        const libraryPane = this.panes.library;

        // Search / sort toolbar — re-render both lists on change.
        libraryPane.addEventListener('input', e => {
            if (e.target.matches('.library-search')) {
                this.filterState.query = e.target.value;
                this._renderLibrary();
            }
        });
        libraryPane.addEventListener('change', e => {
            if (e.target.matches('.library-sort')) {
                this.filterState.sort = e.target.value;
                this._renderLibrary();
            } else if (e.target.matches('.library-import-input')) {
                const file = e.target.files && e.target.files[0];
                e.target.value = ''; // allow re-importing the same file
                if (file) this._handleImportFile(file);
            }
        });

        libraryPane.addEventListener('click', e => {
            const target = e.target;
            const action = target.dataset.action;

            // Pack export/import toolbar buttons (resolve past the inner SVG).
            const packBtn = target.closest('.library-pack-btn');
            if (packBtn) {
                if (packBtn.dataset.action === 'export-pack') this._exportPack();
                else libraryPane.querySelector('.library-import-input')?.click();
                return;
            }

            // Constraint-class filter chip toggle (clicking the active one clears it).
            const constraintChip = target.closest('[data-constraint-filter]');
            if (constraintChip) {
                const cls = constraintChip.dataset.constraintFilter;
                this.filterState.constraint = this.filterState.constraint === cls ? null : cls;
                this._renderConstraintFilters();
                this._renderLibrary();
                return;
            }

            // Tag filter chip toggle.
            const tagChip = target.closest('[data-tag-filter]');
            if (tagChip) {
                const tag = tagChip.dataset.tagFilter;
                this.filterState.tag = this.filterState.tag === tag ? null : tag;
                this._renderTagFilters();
                this._renderLibrary();
                return;
            }

            const sourceFilter = target.closest('[data-source-filter]');
            if (sourceFilter) {
                this.filterState.source = sourceFilter.dataset.sourceFilter;
                this._renderSourceControls();
                this._renderLibrary();
                return;
            }

            const dedupeButton = target.closest('[data-action="remove-public-duplicates"]');
            if (dedupeButton) {
                const duplicates = this.appContext.libraryController.getPublicDuplicateRulesets();
                if (duplicates.length === 0) return;
                EventBus.dispatch(EVENTS.COMMAND_SHOW_CONFIRMATION, {
                    title: 'Remove public duplicates',
                    message: `Remove ${duplicates.length} personal ${duplicates.length === 1 ? 'copy' : 'copies'} that already exist in the public library?\n\nThe public versions stay available. Any personal names, descriptions, tags, thumbnails, or paired starts on those copies will be deleted.`,
                    confirmLabel: `Remove ${duplicates.length}`,
                    onConfirm: () => {
                        const { removed } = this.appContext.libraryController.removePublicDuplicates();
                        EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, {
                            message: `Removed ${removed} duplicate personal ${removed === 1 ? 'ruleset' : 'rulesets'}.`,
                            type: 'success',
                        });
                    },
                });
                return;
            }

            // "Paired start" — what a Load click means for entries that carry an IC (#30).
            const pairedToggle = target.closest('[data-action="toggle-paired-start"]');
            if (pairedToggle) {
                this.pairedStart = !this.pairedStart;
                PersistenceService.saveUISetting('libraryPairedStart', this.pairedStart);
                pairedToggle.classList.toggle('active', this.pairedStart);
                pairedToggle.setAttribute('aria-pressed', String(this.pairedStart));
                return;
            }

            const card = target.closest('.library-card');

            if (action === 'load-rule' || action === 'load-personal') {
                this._loadCard(card);
                return;
            }

            if (target.closest('[data-action="manage-personal"]')) {
                const manageButton = target.closest('[data-action="manage-personal"]');
                const id = card?.dataset.id;
                const rule = this.appContext.libraryController.getUserLibrary().find(r => r.id === id);
                if (!rule) return;

                const popoverActions = [];

                // The other half of the "Paired start" choice, for this one entry — so collapsing
                // the double load button (#30) removes a control, not a capability.
                if (rule.initialState) {
                    popoverActions.push({
                        label: this.pairedStart ? 'Load rule only' : 'Load with paired start',
                        callback: () => this._loadCard(card, { withPairedStart: !this.pairedStart }),
                    });
                }

                popoverActions.push(
                    {
                        label: 'Share on Reddit',
                        callback: () => {
                            const ui = this.appContext.uiManager;
                            if (ui?.shareLibraryEntryToReddit) ui.shareLibraryEntryToReddit(rule);
                        },
                    },
                    {
                        label: 'Edit',
                        callback: () => EventBus.dispatch(EVENTS.COMMAND_SHOW_SAVE_RULESET_MODAL, rule)
                    },
                    {
                        label: 'Copy share link',
                        callback: () => {
                            const url = new URL(window.location.href);
                            url.search = `?r=${rule.hex}`;
                            navigator.clipboard.writeText(url.toString()).then(() => {
                                EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: 'Share link copied!', type: 'success' });
                            });
                        }
                    },
                );

                // Copy the paired initial condition as a portable IC code (only when one exists).
                if (rule.initialState) {
                    popoverActions.push({
                        label: 'Copy IC code',
                        callback: () => {
                            const code = InitialStateCodec.encode(rule.initialState, rule.seed);
                            if (!code) {
                                EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: 'No initial condition to copy.', type: 'info' });
                                return;
                            }
                            navigator.clipboard.writeText(code).then(() => {
                                EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: 'Initial-condition code copied!', type: 'success' });
                            });
                        }
                    });
                }

                // The streamlined route into the curated catalog (#27): validate here, then hand a
                // prefilled GitHub issue to the user. Sits above the manual copy, which stays as the
                // escape hatch for anyone who'd rather open the PR themselves.
                popoverActions.push({
                    label: 'Submit to public library…',
                    callback: () => this._submitToPublicLibrary(rule),
                });

                // One-paste path into a public-library PR: emit the committed rulesets.json shape.
                popoverActions.push({
                    label: 'Copy as public-library JSON',
                    callback: () => {
                        const json = JSON.stringify(toPublicLibraryEntry(rule), null, 2);
                        navigator.clipboard.writeText(json).then(() => {
                            EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: 'Public-library JSON copied — paste it into a rulesets.json PR.', type: 'success' });
                        }).catch(() => {
                            EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: 'Could not access the clipboard.', type: 'error' });
                        });
                    }
                });

                popoverActions.push({
                    label: 'Delete',
                    callback: () => {
                        EventBus.dispatch(EVENTS.COMMAND_SHOW_CONFIRMATION, {
                            title: 'Delete Ruleset',
                            message: `Are you sure you want to permanently delete "${rule.name}"?`,
                            confirmLabel: 'Delete',
                            onConfirm: () => {
                                this.appContext.libraryController.deleteUserRuleset(rule.id);
                                EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: `Deleted "${rule.name}".`, type: 'info' });
                            }
                        });
                    }
                });

                this.actionsPopover.show(popoverActions, manageButton);
            }
        });

        this._subscribeToEvent(EVENTS.USER_LIBRARY_CHANGED, this._onUserLibraryChanged);
    }

    /**
     * Load a card's ruleset. With "Paired start" on (and the entry actually carrying an IC) this is
     * the full dish — ruleset + its initial condition + seed — via the proven explore-find apply
     * path; otherwise it is the rule alone, applied to whatever cells are on screen.
     * @param {HTMLElement|null} card
     * @param {{withPairedStart?: boolean}} [opts] Override the list-level toggle for this one load.
     */
    _loadCard(card, opts = {}) {
        if (!card) return;
        const rule = this._findCardEntry(card);
        const withPaired = opts.withPairedStart ?? this.pairedStart;
        if (withPaired && rule?.initialState) {
            EventBus.dispatch(EVENTS.COMMAND_APPLY_EXPLORE_FIND, {
                find: {
                    hex: rule.hex,
                    initialState: rule.initialState,
                    seed: rule.seed,
                    mnemonic: rule.name || rulesetName(rule.hex),
                    icLabel: rule.initialState.mode,
                },
            });
            return;
        }
        const rac = this.appContext.rulesetActionController;
        this.appContext.libraryController.loadRuleset(card.dataset.hex, rac.getGenScope(), rac.getGenAutoReset());
    }

    /** Resolve a card back to its marked source; duplicates therefore keep their own metadata/recipe. */
    _findCardEntry(card) {
        if (!card) return null;
        const id = card.dataset.id;
        const hex = card.dataset.hex;
        const source = card.dataset.source;
        const user = this.appContext.libraryController.getUserLibrary();
        if (id) {
            const byId = user.find(r => r.id === id);
            if (byId) return byId;
        }
        if (source === 'public') {
            return (this.libraryData?.rulesets || []).find(r => r.hex === hex) || null;
        }
        return user.find(r => r.hex === hex) || null;
    }

    _onUserLibraryChanged = () => {
        this._renderConstraintFilters();
        this._renderTagFilters();
        this._renderSourceControls();
        this._renderLibrary();
        this._scheduleThumbnailBackfill();
    };

    /**
     * "Submit to public library" (#27) — propose a personal entry for the committed catalog.
     *
     * Two gates, both here rather than on the reviewer: the entry has to clear the intake bar
     * ({@link validateSubmission} — named, described, tagged, replayable, not already public), and the
     * user has to confirm what leaves the browser. A failed check is not a dead end: the same dialog
     * offers the editor that fixes it.
     * @param {object} rule Personal-library entry
     */
    _submitToPublicLibrary(rule) {
        const publicHexes = (this.libraryData?.rulesets || []).map(r => r.hex);
        const { ok, problems } = validateSubmission(rule, { publicHexes });
        const label = rule.name || rulesetName(rule.hex);

        if (!ok) {
            EventBus.dispatch(EVENTS.COMMAND_SHOW_CONFIRMATION, {
                title: 'Not ready for the public library',
                message: `"${label}" needs a little more before it can be proposed:\n\n${problems.map(p => `• ${p}`).join('\n')}\n\nOpen the editor to fill these in?`,
                confirmLabel: 'Edit entry',
                onConfirm: () => EventBus.dispatch(EVENTS.COMMAND_SHOW_SAVE_RULESET_MODAL, rule),
            });
            return;
        }

        // Deep-link the reviewer back at THIS build's origin (a local or preview copy reviews itself),
        // trimmed to its directory so `index.html` doesn't end up in the base.
        const origin = `${window.location.origin}${window.location.pathname}`.replace(/[^/]*$/, '');
        const submission = buildSubmissionIssue(rule, { origin });
        EventBus.dispatch(EVENTS.COMMAND_SHOW_CONFIRMATION, {
            title: 'Submit to the public library',
            message: `This opens a prefilled GitHub issue proposing "${label}" for the bundled library.\n\nIt carries the name, description, tags, ruleset code and paired start — no thumbnail and nothing else from this browser. You'll be credited by your GitHub username unless the form's Credit field says otherwise.\n\nA GitHub account is needed to post it.`,
            confirmLabel: 'Open GitHub',
            onConfirm: () => this._openSubmissionIssue(submission),
        });
    }

    /**
     * Open a built submission. When the entry is too big to ride the URL it goes on the clipboard
     * instead and the form opens empty — the copy is STARTED before `window.open` (same task, so the
     * confirm click's transient activation still covers the popup) and only reported on afterwards.
     * @param {{url: string, entryJson: string, oversize: boolean}} submission
     */
    _openSubmissionIssue(submission) {
        const copy = submission.oversize
            ? navigator.clipboard.writeText(submission.entryJson).then(() => true, () => false)
            : Promise.resolve(null);
        const opened = window.open(submission.url, '_blank', 'noopener,noreferrer');

        copy.then(copied => {
            if (!opened) {
                EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, {
                    message: 'GitHub was blocked from opening — allow popups for this site, or use "Copy as public-library JSON" and open an issue yourself.',
                    type: 'error',
                });
                return;
            }
            if (copied === null) {
                EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, {
                    message: 'Submission form opened with your entry filled in — check the Credit field, then post it.',
                    type: 'success',
                });
            } else if (copied) {
                EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, {
                    message: 'Entry was too long for the link, so it is on your clipboard — paste it into "Library entry" on GitHub.',
                    type: 'info',
                });
            } else {
                EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, {
                    message: 'Entry was too long for the link and the clipboard was blocked — use "Copy as public-library JSON", then paste it into the form.',
                    type: 'error',
                });
            }
        });
    }

    /** Download the personal library as a dated pack file (no-op with a toast when it's empty). */
    _exportPack() {
        const lc = this.appContext.libraryController;
        if (lc.getUserLibrary().length === 0) {
            EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: 'No saved rulesets to export yet.', type: 'info' });
            return;
        }
        const date = new Date().toISOString().slice(0, 10);
        downloadFile(`hexlife-rulesets-${date}.json`, lc.exportPackJSON(), 'application/json');
    }

    /** Read + decode a chosen pack file, then confirm-gate the merge and toast the result. */
    async _handleImportFile(file) {
        let decoded;
        try {
            decoded = decodePack(await file.text());
        } catch (err) {
            EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: `Import failed: ${err.message}`, type: 'error' });
            return;
        }
        const rulesets = decoded.rulesets;
        if (rulesets.length === 0) {
            const detail = decoded.finds.length > 0 ? ' (this pack only contains explore finds — import it from the Explore panel).' : '.';
            EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: `No importable rulesets in that file${detail}`, type: 'info' });
            return;
        }
        const preview = rulesets.slice(0, 5).map(r => `• ${r.name}`).join('\n');
        const more = rulesets.length > 5 ? `\n…and ${rulesets.length - 5} more` : '';
        const warnLine = decoded.warnings.length ? `\n\n${decoded.warnings.length} item(s) were cleaned up on import.` : '';
        EventBus.dispatch(EVENTS.COMMAND_SHOW_CONFIRMATION, {
            title: 'Import rulesets',
            message: `Add ${rulesets.length} ruleset(s) to your personal library? Duplicates (same rule) are skipped.\n\n${preview}${more}${warnLine}`,
            confirmLabel: 'Import',
            onConfirm: () => {
                const { added, skipped } = this.appContext.libraryController.importRulesets(rulesets);
                const msg = added > 0
                    ? `Imported ${added} ruleset(s)${skipped ? `, skipped ${skipped} duplicate(s)` : ''}.`
                    : `Nothing new to import — all ${skipped} already in your library.`;
                EventBus.dispatch(EVENTS.COMMAND_SHOW_TOAST, { message: msg, type: added > 0 ? 'success' : 'info' });
            },
        });
    }

    destroy() {
        this._backfillHandle?.cancel();
        // The converter subscribes to RULESET_CHANGED / SELECTED_WORLD_CHANGED to stay a live
        // readout; those subscriptions outlive the panel unless we tear it down explicitly.
        this.converter?.destroy();
        this.converter = null;
        super.destroy?.();
    }

    setActivePane(paneName) {
        for (const key in this.panes) {
            this.panes[key].classList.add('hidden');
            this.segments[key].classList.remove('active');
        }
        this.panes[paneName].classList.remove('hidden');
        this.segments[paneName].classList.add('active');
    }
}
